// ABOUTME: The bottom navigation: Zeiterfassung, Jobs and Profil as three labelled, equal destinations, the active
// ABOUTME: one read from the route on every render. Jobs is there for a talent in the tracker arm only.
import { Link, useLocation } from 'react-router-dom';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import Paper from '@mui/material/Paper';
import { PATHS } from 'config/paths';
import { sendNavDestinationTapped } from 'modules/analytics';
import { useTrackerArm } from 'modules/applications';
import { getTrans } from 'modules/i18n';
import { NAV_HEIGHT } from '../constants';
import { NavAction } from '../types';

/**
 * In the order they sit on the bar. Jobs leads to the tracker, which exists only for a talent in the PRO_ATS
 * tracker arm; for everyone else the bar has the two destinations it always had, now with labels.
 */
const DESTINATIONS: NavAction[] = [
  {
    destination: 'weeks',
    path: PATHS.weeks,
    icon: <WorkOutlineIcon />,
    label: 'nav.timeTracking'
  },
  {
    destination: 'applications',
    path: PATHS.applications,
    // A job opened from the feed lives under /jobs and is still the Jobs destination.
    alsoUnder: [PATHS.jobs],
    icon: <BookmarkBorderIcon />,
    label: 'nav.jobs'
  },
  {
    destination: 'account',
    path: PATHS.account,
    icon: <PersonOutlineIcon />,
    label: 'nav.profile'
  }
];

/** A destination is active on its own route and on every route under it (`/account/language` lights up Profil). */
const isUnder = (pathname: string, path: string): boolean =>
  pathname === path || pathname.startsWith(`${path}/`);

export const Footer = () => {
  const { pathname } = useLocation();
  const { isTracker } = useTrackerArm();

  const shown = DESTINATIONS.filter(
    ({ destination }) => destination !== 'applications' || isTracker
  );
  // Derived from the route, never kept in state: a navigation that did not come through the bar (a back gesture, a
  // deep link, a redirect) must move the highlight too.
  const active =
    shown.find(({ path, alsoUnder = [] }) =>
      [path, ...alsoUnder].some(candidate => isUnder(pathname, candidate))
    )?.path ?? false;

  return (
    <Paper
      data-testid="bottom-nav"
      square
      elevation={0}
      sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        height: NAV_HEIGHT,
        borderTop: '1px solid #eee',
        zIndex: theme => theme.zIndex.appBar
      }}
    >
      <BottomNavigation
        value={active}
        showLabels
        sx={{ height: '100%', justifyContent: { sm: 'space-evenly' } }}
      >
        {shown.map(({ destination, path, icon, label }) => (
          <BottomNavigationAction
            key={path}
            component={Link}
            to={path}
            value={path}
            icon={icon}
            label={getTrans(label)}
            onClick={() => sendNavDestinationTapped({ destination })}
          />
        ))}
      </BottomNavigation>
    </Paper>
  );
};
